import { Card, Col, Row, Tag, Tooltip } from "antd";
import { GoogleOutlined } from "@ant-design/icons";
import { CSSProperties } from "react";
import infoIcon from '../../../../assets/icons/HOME-ICON-light.png';
import MathUtils from "../../../../common/utils/MathUtils";

type OpenStatusFilter = 'open' | 'closed' | null;

type BlockInfoKpisProps = {
    kpis: any;
    openStatusFilter?: OpenStatusFilter;
    onOpenStatusFilterChange?: (filter: OpenStatusFilter) => void;
    compact?: boolean;
};

const openTagStyle = (active: boolean, clickable: boolean): CSSProperties => ({
    verticalAlign: 'middle',
    borderRadius: '8px',
    color: '#4A90E2',
    background: active ? '#cfe3f8' : '#e8f1fb',
    border: active ? '1.5px solid #4A90E2' : '1px solid transparent',
    cursor: clickable ? 'pointer' : 'default',
    fontWeight: 500,
    fontSize: 13,
    padding: '1px 8px',
});

const closedTagStyle = (active: boolean, clickable: boolean): CSSProperties => ({
    verticalAlign: 'middle',
    borderRadius: '8px',
    color: 'crimson',
    background: active ? '#ffcccc' : '#ffe5e5',
    border: active ? '1.5px solid crimson' : '1px solid transparent',
    cursor: clickable ? 'pointer' : 'default',
    fontWeight: 500,
    fontSize: 13,
    padding: '1px 8px',
});

const kpiLabelStyle = (compact: boolean): CSSProperties => ({
    fontSize: compact ? 11 : 12,
    color: '#9F9F9F',
    fontWeight: 500,
    lineHeight: 1.2,
    marginTop: 2,
});

export const BlockInfoKpis = (props: BlockInfoKpisProps) => {
    const { kpis, openStatusFilter = null, onOpenStatusFilterChange, compact = false } = props;
    const clickable = !!onOpenStatusFilterChange;

    const toggleFilter = (value: 'open' | 'closed') => {
        if (!onOpenStatusFilterChange) return;
        onOpenStatusFilterChange(openStatusFilter === value ? null : value);
    };

    const locationsWithAlerts = kpis?.numberOfLocationsWithUpdates ?? 0;
    const locationsNonOperable = kpis?.numberOfLocationsNonOperable ?? 0;
    const showNonOperable = locationsNonOperable > 0;
    const googleStatusColor = showNonOperable
        ? 'MediumVioletRed'
        : locationsWithAlerts > 0
            ? '#fa8c16'
            : 'lightgrey';
    const googleStatusLabel = showNonOperable
        ? `${locationsNonOperable} locales no operables`
        : locationsWithAlerts > 0
            ? `${locationsWithAlerts} locales con alertas`
            : 'No hay locales con alertas';
    const googleStatusTooltip = showNonOperable
        ? 'Locales no operables en Google'
        : 'Locales con alertas de validación pendiente o cambios de Google';

    const cardHeight = compact ? 88 : 120;
    const cardRadius = compact ? '16px' : '25px';
    const iconHeight = compact ? 40 : 56;
    const valueFontSize = compact ? 28 : 36;
    const googleIconSize = compact ? 24 : 32;
    const statusNumberSize = compact ? 20 : 25;
    const cardBody: CSSProperties = {
        backgroundColor: 'white',
        borderRadius: cardRadius,
        height: cardHeight,
        padding: compact ? '10px 14px' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <>
            <Row gutter={compact ? 16 : 36} style={{ marginBottom: compact ? 8 : 20 }}>
                <Col span={6}>
                    <Card bodyStyle={cardBody}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: compact ? 10 : 14 }}>
                            <img src={infoIcon} style={{ height: iconHeight, borderRadius: '17px' }} />
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span style={{ fontSize: valueFontSize, fontWeight: 700, lineHeight: 1 }}>
                                    {kpis === null ? '-' : kpis?.numberOfLocations}
                                </span>
                                <span style={kpiLabelStyle(compact)}>Locales</span>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bodyStyle={cardBody}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <span style={{ fontSize: compact ? '22px' : '28px' }}>
                                <Row>
                                    <Col>
                                        <Tag
                                            style={openTagStyle(openStatusFilter === 'open', clickable)}
                                            onClick={() => toggleFilter('open')}
                                        >
                                            <b>Abiertos</b>
                                        </Tag>
                                    </Col>
                                    <Col>
                                        <b style={{ fontSize: statusNumberSize }}>{kpis === null ? '-' : kpis?.numberOfLocations}</b>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Tag
                                            style={closedTagStyle(openStatusFilter === 'closed', clickable)}
                                            onClick={() => toggleFilter('closed')}
                                        >
                                            <b>Cerrados</b>
                                        </Tag>
                                    </Col>
                                    <Col>
                                        <b style={{ fontSize: statusNumberSize }}>{kpis === null ? '-' : kpis?.closed + kpis?.closedPermanently}</b>
                                    </Col>
                                </Row>
                            </span>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bodyStyle={cardBody}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: valueFontSize, fontWeight: 'bold', lineHeight: 1 }}>
                                {kpis === null || typeof kpis?.completedListing !== 'number'
                                    ? '-'
                                    : `${MathUtils.round(100 * kpis.completedListing, 1)}%`}
                            </span>
                            <span style={kpiLabelStyle(compact)}>Info Completada</span>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bodyStyle={cardBody}>
                        <Tooltip title={googleStatusTooltip}>
                            <div style={{
                                color: googleStatusColor,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 4,
                            }}>
                                <GoogleOutlined style={{ fontSize: googleIconSize }} />
                                <b style={{ fontSize: compact ? 12 : 13, textAlign: 'center', lineHeight: 1.2, fontWeight: 600 }}>
                                    {googleStatusLabel}
                                </b>
                            </div>
                        </Tooltip>
                    </Card>
                </Col>
            </Row>
        </>
    )
}
